"use client"

import React, { useState, useRef } from "react"
import { motion, AnimatePresence, useMotionValue, useTransform, useSpring } from "framer-motion"

export function LocationMap({
    location = "Jaysingpur, Maharashtra",
    coordinates = "16.7775° N, 74.5523° E",
    className,
}) {
    const [isHovered, setIsHovered] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
    const containerRef = useRef(null)

    React.useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const mouseX = useMotionValue(0)
    const mouseY = useMotionValue(0)

    const rotateX = useTransform(mouseY, [-50, 50], [8, -8])
    const rotateY = useTransform(mouseX, [-50, 50], [-8, 8])

    const springRotateX = useSpring(rotateX, { stiffness: 300, damping: 30 })
    const springRotateY = useSpring(rotateY, { stiffness: 300, damping: 30 })

    const handleMouseMove = (e) => {
        if (!containerRef.current) return
        const rect = containerRef.current.getBoundingClientRect()
        const centerX = rect.left + rect.width / 2
        const centerY = rect.top + rect.height / 2
        mouseX.set(e.clientX - centerX)
        mouseY.set(e.clientY - centerY)
    }

    const handleMouseLeave = () => {
        mouseX.set(0)
        mouseY.set(0)
        setIsHovered(false)
    }

    const handleClick = () => {
        if (!isExpanded) setIsExpanded(true)
    }

    return (
        <motion.div
            ref={containerRef}
            className={`relative select-none ${className} ${!isExpanded ? 'cursor-pointer' : ''}`}
            style={{
                perspective: 1000,
            }}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
        >
            <motion.div
                className="relative overflow-hidden rounded-3xl bg-white border border-gray-100 shadow-xl shadow-gray-200/50 max-w-[calc(100vw-2rem)]"
                style={{
                    rotateX: isExpanded ? 0 : springRotateX, // Flatten when expanded for iframe
                    rotateY: isExpanded ? 0 : springRotateY,
                    transformStyle: "preserve-3d",
                }}
                animate={{
                    width: isExpanded ? Math.min(windowWidth - 32, 500) : 280,
                    height: isExpanded ? (windowWidth < 640 ? 300 : 350) : 160,
                }}
                transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 35,
                }}
            >
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 via-transparent to-gray-50/80 pointer-events-none" />

                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            className="absolute inset-0 z-20"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 }}
                        >
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m23!1m12!1m3!1d15279.591216780225!2d74.55804495!3d16.7817593!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m8!3e6!4m0!4m5!1s0x3bc11f0778d850b7%3A0xfbad807d653e9612!2sDr%20Ajinkya&#39;s%20DentoCare%2C%20First%20Floor%2C%20Kolhapur%20-%20Sangli%20Hwy%2C%20above%20Med%20Plus%2C%20opposite%20to%20Post%20Office%2C%20Purtiwadi%2C%20Jaysingpur%2C%20Kolhapur%2C%20Maharashtra%20416101!3m2!1d16.7775236!2d74.5522621!5e0!3m2!1sen!2sin!4v1772295055907!5m2!1sen!2sin"
                                className="w-full h-full border-0 absolute inset-0 z-10"
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            ></iframe>

                            {/* Close Button overlay */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsExpanded(false);
                                }}
                                className="absolute top-4 right-4 z-30 bg-black text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-gray-800 transition transform hover:scale-105"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Grid pattern - only show when collapsed */}
                <motion.div
                    className="absolute inset-0 opacity-[0.05]"
                    animate={{ opacity: isExpanded ? 0 : 0.05 }}
                    transition={{ duration: 0.3 }}
                >
                    <svg width="100%" height="100%" className="absolute inset-0">
                        <defs>
                            <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
                                <path d="M 30 0 L 0 0 0 30" fill="none" stroke="black" strokeWidth="0.5" />
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#grid)" />
                    </svg>
                </motion.div>

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-6">
                    {/* Top section */}
                    <div className="flex items-start justify-between">
                        <div className="relative">
                            <motion.div
                                className="relative"
                                animate={{
                                    opacity: isExpanded ? 0 : 1,
                                }}
                                transition={{ duration: 0.3 }}
                            >
                                {/* Map Icon SVG */}
                                <motion.svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="text-black"
                                    animate={{
                                        scale: isHovered ? 1.1 : 1,
                                    }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
                                    <line x1="9" x2="9" y1="3" y2="18" />
                                    <line x1="15" x2="15" y1="6" y2="21" />
                                </motion.svg>
                            </motion.div>
                        </div>

                        {/* Status indicator */}
                        <motion.div
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/5 backdrop-blur-sm border border-black/5"
                            animate={{
                                scale: isHovered ? 1.05 : 1,
                                backgroundColor: isHovered ? "rgba(0,0,0,0.08)" : "rgba(0,0,0,0.05)",
                            }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
                            <span className="text-[10px] font-bold text-black tracking-[0.2em] uppercase font-body">Location</span>
                        </motion.div>
                    </div>

                    {/* Bottom section */}
                    <div className="space-y-1.5">
                        <motion.h3
                            className="text-black font-bold text-base tracking-tight font-heading"
                            animate={{
                                x: isHovered ? 4 : 0,
                            }}
                            transition={{ type: "spring", stiffness: 400, damping: 25 }}
                        >
                            {location}
                        </motion.h3>

                        <AnimatePresence>
                            {isExpanded && (
                                <motion.p
                                    className="text-black/50 text-[11px] font-medium font-body"
                                    initial={{ opacity: 0, y: -10, height: 0 }}
                                    animate={{ opacity: 1, y: 0, height: "auto" }}
                                    exit={{ opacity: 0, y: -10, height: 0 }}
                                    transition={{ duration: 0.25 }}
                                >
                                    {coordinates}
                                </motion.p>
                            )}
                        </AnimatePresence>

                        {/* Animated underline */}
                        <motion.div
                            className="h-[2px] bg-black"
                            initial={{ scaleX: 0, originX: 0 }}
                            animate={{
                                scaleX: isHovered || isExpanded ? 1 : 0.2,
                            }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                        />
                    </div>
                </div>

            </motion.div>

            {/* Click hint */}
            <motion.p
                className="absolute -bottom-8 left-1/2 text-[10px] text-black/40 font-bold tracking-widest uppercase"
                style={{ x: "-50%" }}
                initial={{ opacity: 0 }}
                animate={{
                    opacity: isHovered && !isExpanded ? 1 : 0,
                    y: isHovered ? 0 : 4,
                }}
                transition={{ duration: 0.2 }}
            >
                Click to expand map
            </motion.p>
        </motion.div>
    )
}
